import * as yup from 'yup';
import { ContactTypes, ErrorMessages } from '@src/utils/enums';
import { ContactAnswer } from '@src/app-components/dynamic-form/types';
import _startCase from 'lodash/startCase';
import _unique from 'lodash/uniq';
import { AquaticResourceRow, YupTestContext, RequestReasonValue, JdPayload } from './JDForm.types';

export const JDFormAquaticResourcesMetadata = {
  sectionName: 'Aquatic Resource Inventory',
};

export enum JDFormErrorMessages {
  DefaultValueReserved = 'DEFAULT_VALUE is a reserved word, please enter a valid string',
  JDTypeNotSelected = 'You have not selected that the USACE provide a written Jurisdictional Determination. This selection must match your aquatic resource JD type.',
  JDTypeMismatch = 'The selected Jurisdictional Determination type does not match the types listed in the Aquatic Resources table. These selections must match.',
}

export const RELATIONS_OPTIONS_LIST = [
  'I currently own this property.',
  'I plan to purchase this property.',
  'I am an agent/consultant acting on behalf of the applicant.',
  'Other',
] as const;

export const CHECKBOX_VALUES = {
  Preliminary: ['Preliminary'],
  Approved: ['Approved'],
  'Preliminary+Approved': ['Preliminary', 'Approved'],
} as const;

export const getIsArDeterminationMismatch = ({
  arRows = [],
  jurisdictionalDetermination,
}: {
  arRows?: AquaticResourceRow[];
  jurisdictionalDetermination: string[];
}): boolean => {
  const arJdTypes = _unique(arRows.map((row: AquaticResourceRow) => row.jurisdictionalDeterminationType)).filter(
    (type: string | undefined): type is string => !!type
  );

  return arJdTypes.reduce(
    (acc: boolean, arJdType: string) => (!jurisdictionalDetermination.includes(arJdType) ? true : acc),
    false
  );
};

export const getIsWrittenJdMismatch = ({
  arRows = [],
  jdTypeKnown,
}: {
  arRows?: AquaticResourceRow[];
  jdTypeKnown: string;
}): boolean => {
  if (jdTypeKnown === 'true') {
    return false;
  }

  const arJdTypes = _unique(arRows.map((row: AquaticResourceRow) => row.jurisdictionalDeterminationType));
  return arJdTypes.reduce(
    (acc: boolean, arJdType: string | undefined) =>
      !['No JD Requested', null].includes(arJdType as string | null) ? true : acc,
    false
  );
};

export const getJdTypeString = (str: string) => {
  const upperStr = str?.toUpperCase();
  if (upperStr === 'NO JD REQUESTED') {
    return 'No JD Requested';
  } else {
    return _startCase(str);
  }
};
export const formatJdPayload = (payload: JdPayload): JdPayload => {
  const request = payload.request;
  const jdRequest = request?.jdRequests?.[0];

  if (!request || !jdRequest) {
    return payload;
  }

  let contacts = request.contacts;

  if (contacts && jdRequest.hasAddPropertyOwners === 'false') {
    contacts = contacts.filter((contact: ContactAnswer) => contact.contactType !== ContactTypes.PropertyOwner);
  }

  return {
    ...payload,
    request: {
      ...request,
      ...(jdRequest.requestType && {
        requestType: jdRequest.requestType,
      }),
      contacts,
    },
  };
};

export const getIsJdTypeMismatch = ({
  jdTypes = [],
  jurisdictionalDetermination = [],
}: {
  jdTypes?: string[];
  jurisdictionalDetermination?: string[];
}): boolean => {
  const selectedTypes = jurisdictionalDetermination.map((type) => type.trim().toLowerCase());
  return jdTypes.some((type) => !selectedTypes.includes(type.trim().toLowerCase()));
};

export const getJDValidationSchema = () =>
  yup.object().shape({
    requestReason: yup.array().min(1, ErrorMessages.MultiSelectOption),
    otherRequestReason: yup
      .string()
      .when('requestReason', {
        is: (val: RequestReasonValue[]) => val?.map((v: RequestReasonValue) => v.value).includes('Other'),
        then: () => yup.string().required(ErrorMessages.Required),
      })
      .nullable(),
    relationshipToRequest: yup
      .string()
      .test(
        'isDefault',
        JDFormErrorMessages.DefaultValueReserved,
        (val: string) => val.toUpperCase() !== 'DEFAULT_VALUE'
      )
      .required(ErrorMessages.SelectOption),
    otherRelationshipToRequest: yup
      .string()
      .when('relationshipToRequest', { is: 'Other', then: () => yup.string().required(ErrorMessages.Required) })
      .nullable(),
    jdTypeKnown: yup
      .string()
      .oneOf(['true', 'false'], ErrorMessages.SelectOption)
      .test('jd-type-known-false', JDFormErrorMessages.JDTypeNotSelected, function (this: YupTestContext, val: string) {
        return val === 'true' || (val === 'false' && !this.parent.writtenJdMismatch);
      }),
    jdTypeMismatch: yup.boolean().nullable(),
    writtenJdMismatch: yup.boolean().nullable(),
    jurisdictionalDetermination: yup
      .array()
      .test('jd-type-known', ErrorMessages.MultiSelectOption, function (this: YupTestContext, determination: string[]) {
        return this.parent.jdTypeKnown === 'false' || (this.parent.jdTypeKnown === 'true' && determination?.length > 0);
      })
      .test('jd-type-mismatch', JDFormErrorMessages.JDTypeMismatch, function (this: YupTestContext) {
        return (
          this.parent.jdTypeKnown === 'false' || (this.parent.jdTypeKnown === 'true' && !this.parent.jdTypeMismatch)
        );
      }),
  });
